import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX } from "@/api/client";

/**
 * Read-side endpoints behind the Finance overview (backend i56).
 * Every amount here is an INTEGER in minor units (centavos).
 */

export interface CurrencyKpis {
  currency: string;
  gmv_minor: number;
  captured_count: number;
  platform_revenue_minor: number;
  refunds_requested_minor: number;
  refunds_paid_minor: number;
  pending_payment_count: number;
  pending_payment_minor: number;
  failed_payment_count: number;
  payouts_owed_minor: number;
  payouts_owed_count: number;
  payouts_held_count: number;
  payouts_sent_minor: number;
  refunds_open_minor: number;
  refunds_open_count: number;
}

export interface FinanceOverview {
  window: { from: string; to: string };
  currencies: string[];
  primary_currency: string;
  kpis: Record<string, CurrencyKpis>;
  daily: { day: string; currency: string; count: number; amount_minor: number }[];
  payouts_by_status: { status: string; currency: string; count: number; amount_minor: number }[];
  refunds_by_status: {
    status: string;
    currency: string;
    count: number;
    amount_minor: number;
    oldest_requested_at: string | null;
  }[];
  settlements_by_status: {
    status: string;
    count: number;
    expected_minor: number;
    settled_minor: number;
  }[];
  fee: { computed_count: number; unset_count: number; unknown_count: number };
  queues: {
    refunds_open: number;
    payouts_to_action: number;
    exceptions_open: number;
    exceptions_open_critical: number;
    disputes_open: number;
    settlements_unmatched: number;
    commission_rules_active: number;
    commission_rules_draft: number;
  };
}

export interface IntegrityCheck {
  code: string;
  severity: "critical" | "high" | "normal" | string;
  title: string;
  description: string;
  count: number;
  sample_ids: string[];
  entity: string;
}

export type ExportKind = "transactions" | "refunds" | "payouts" | "settlements";

export interface ExportResult {
  csv: string;
  row_count: number;
  truncated: boolean;
  kind: ExportKind;
  window: { from: string; to: string };
}

const BASE = `${API_VERSION_PREFIX}/hq/finance`;

export function createFinanceOverviewApi(client: ApiClient) {
  return {
    getOverview(from: string, to: string) {
      const qs = new URLSearchParams({ from, to }).toString();
      return client.get<{ overview: FinanceOverview }>(`${BASE}/overview?${qs}`);
    },
    getIntegrityChecks() {
      return client.get<{
        checks: IntegrityCheck[];
        violations: number;
        checked_at: string;
      }>(`${BASE}/integrity-checks`);
    },
    exportCsv(kind: ExportKind, from: string, to: string) {
      const qs = new URLSearchParams({ from, to }).toString();
      return client.get<ExportResult>(`${BASE}/exports/${kind}?${qs}`);
    },
  };
}
